import { DateTime } from "luxon";
import { useCallback, useState } from "react";
import logoImage from "../../../assets/img/aerscape.png";
import { formatDateTime } from "../../../utils/dates";

// Cache the watermark image to avoid reloading on each download
let cachedWatermark: HTMLImageElement | null = null;

/**
 * Load the watermark image and cache it
 */
const loadWatermark = async (): Promise<HTMLImageElement> => {
    if (cachedWatermark) {
        return cachedWatermark;
    }

    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
            cachedWatermark = img;
            resolve(img);
        };
        img.onerror = () => {
            reject(new Error("Failed to load watermark image"));
        };
        img.src = logoImage;
    });
};

/**
 * Extract SVG dimensions with fallback strategy
 */
const getSvgDimensions = (
    svgElement: SVGSVGElement,
): { width: number; height: number } => {
    // Try viewBox attribute first (most reliable for D3 plots)
    const viewBox = svgElement.getAttribute("viewBox");
    if (viewBox) {
        const [, , width, height] = viewBox.split(/\s+|,/).map(parseFloat);
        if (width > 0 && height > 0) {
            return { width, height };
        }
    }

    // Fall back to getBoundingClientRect
    const { width, height } = svgElement.getBoundingClientRect();
    return width > 0 && height > 0
        ? { width, height }
        : { width: 800, height: 600 };
};

/**
 * Custom hook for downloading plot images as JPEG with watermark
 *
 * @param svgRef - Reference to the SVG element to export
 * @param baseFilename - Base filename without extension or timestamp
 * @param extraText - Optional extra text to append to the base text at the bottom
 * @returns Object with downloadImage callback and isDownloading state
 */
export const useImageDownload = (
    svgRef: React.MutableRefObject<SVGSVGElement | null>,
    baseFilename: string,
    extraText?: string,
) => {
    const [isDownloading, setIsDownloading] = useState(false);

    const downloadImage = useCallback(async () => {
        const svgElement = svgRef.current;
        if (!svgElement) {
            console.error("SVG element not found");
            return;
        }

        setIsDownloading(true);
        try {
            // Get SVG dimensions
            const { width, height } = getSvgDimensions(svgElement);

            // Add padding to prevent clipping of rotated text (like y-axis labels)
            const padding = 20;
            const canvasWidth = width + padding * 2;
            const canvasHeight = height + padding * 2;

            // Clone SVG and set font-family to sans-serif
            const svgClone = svgElement.cloneNode(true) as SVGSVGElement;
            svgClone.style.fontFamily = "sans-serif";

            // Serialize SVG to string
            const svgData = new XMLSerializer().serializeToString(svgClone);
            const svgBlob = new Blob([svgData], {
                type: "image/svg+xml;charset=utf-8",
            });
            const svgUrl = URL.createObjectURL(svgBlob);

            try {
                // Load SVG as image
                const svgImage = await new Promise<HTMLImageElement>(
                    (resolve, reject) => {
                        const img = new Image();
                        img.onload = () => resolve(img);
                        img.onerror = () =>
                            reject(new Error("Failed to load SVG"));
                        img.src = svgUrl;
                    },
                );

                // Create canvas with 4x scaling for quality
                const canvas = document.createElement("canvas");
                const scaleFactor = 4;
                const scaledWidth = canvasWidth * scaleFactor;
                const scaledHeight = canvasHeight * scaleFactor;
                canvas.width = scaledWidth;
                canvas.height = scaledHeight;

                const ctx = canvas.getContext("2d");
                if (!ctx) {
                    throw new Error("Failed to get canvas context");
                }

                // Scale context for higher quality
                ctx.scale(scaleFactor, scaleFactor);

                // Fill with white background
                ctx.fillStyle = "white";
                ctx.fillRect(0, 0, canvasWidth, canvasHeight);

                // Draw SVG image with padding offset
                ctx.drawImage(svgImage, padding, padding, width, height);

                // Reset scale for watermark drawing
                ctx.setTransform(1, 0, 0, 1, 0, 0);

                // Add diagonal watermark pattern
                try {
                    const watermarkImg = await loadWatermark();
                    const aspectRatio =
                        watermarkImg.naturalHeight / watermarkImg.naturalWidth;

                    // Set semi-transparent opacity
                    ctx.globalAlpha = 0.1;

                    // Save the current context state
                    ctx.save();

                    // Restore the context state
                    ctx.restore();

                    // Add large watermark in top-right corner
                    const largeLogoWidth = Math.min(scaledWidth * 0.15, 800);
                    const largeLogoHeight = largeLogoWidth * aspectRatio;
                    const x = scaledWidth - largeLogoWidth - 100;
                    const y = 150;

                    ctx.globalAlpha = 1.0;
                    ctx.drawImage(
                        watermarkImg,
                        x,
                        y,
                        largeLogoWidth,
                        largeLogoHeight,
                    );
                } catch (error) {
                    console.warn(
                        "Failed to load watermark, continuing without it:",
                        error,
                    );
                }

                // Add "Experimental - Preliminary Analytics" text at the bottom
                let fullText = "Experimental - Preliminary Analytics. ";
                if (extraText) {
                    fullText += `${extraText}. `;
                }
                fullText += `Created at ${formatDateTime(DateTime.now())}.`;

                ctx.globalAlpha = 1.0;
                ctx.fillStyle = "#666666";
                ctx.font = `${6 * scaleFactor}px sans-serif`;
                ctx.textAlign = "start";
                ctx.textBaseline = "bottom";
                ctx.fillText(
                    fullText,
                    scaleFactor * 10,
                    scaledHeight - 15 * scaleFactor,
                );

                ctx.fillText(
                    "Portions of the downloaded data from AERSHED may " +
                        "have been obtained or derived from underlying third - " +
                        "party sources.",
                    scaleFactor * 10,
                    scaledHeight - 9 * scaleFactor,
                );
                ctx.fillText(
                    "Please see the Licensing page " +
                        `(${window.location.origin}/licensing) within AERSHED ` +
                        "for the applicable licensing requirements for all third - party sources.",
                    scaleFactor * 10,
                    scaledHeight - 3 * scaleFactor,
                );

                // Convert to JPEG
                const jpegBlob = await new Promise<Blob>((resolve, reject) => {
                    canvas.toBlob(
                        (blob) => {
                            if (blob) {
                                resolve(blob);
                            } else {
                                reject(new Error("Failed to create JPEG blob"));
                            }
                        },
                        "image/jpeg",
                        0.92,
                    );
                });

                // Generate filename with timestamp
                const timestamp = new Date().toISOString().split("T")[0];
                const filename = `${baseFilename}_${timestamp}.jpg`;

                // Download the image
                const downloadUrl = URL.createObjectURL(jpegBlob);
                const a = document.createElement("a");
                a.href = downloadUrl;
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(downloadUrl);
            } finally {
                URL.revokeObjectURL(svgUrl);
            }
        } catch (error) {
            console.error("Failed to download image:", error);
        } finally {
            setIsDownloading(false);
        }
    }, [svgRef, baseFilename, extraText]);

    return { downloadImage, isDownloading };
};
